import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {EntityKey} from '../entity/entity';
import {GuruAllowable} from '../model/guru-allowable';
import {GuruEntity} from '../model/guru-entity';
import {RoleName} from '../person/person-login';
import {ModelService} from '../services/model-service';
import {PersonLoginService} from '../services/person-login-service';
import {SpecificationService} from '../services/specification-service';
import {StringUtils} from '../utils/string-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';

import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'project-defaults',
    templateUrl: './project-defaults.component.html',
    styleUrls: ['./project-defaults.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, FormsModule, MatProgressSpinner]
})
export class ProjectDefaultsComponent implements OnInit, OnChanges {
  readonly entityName = input.required<string>({ alias: 'entity-name' });
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly specificationService = inject(SpecificationService);
  private readonly modelService = inject(ModelService);
  private readonly router = inject(Router);


  private readonly propertyName: string = 'ProjectDefaults';
  private readonly engineKey = signal<string>(undefined!);
  private readonly entityKey = signal<EntityKey>(undefined!);
  private readonly invalidEntities = signal<string[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly userSelections = signal<GuruEntity[]>(undefined!);
  readonly isComplete = signal<boolean>(undefined!);
  readonly isValid = signal<boolean>(undefined!);
  readonly selectedValues = signal<GuruEntity[]>(undefined!);

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    const entityName: string = this.entityName();
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (entityName && entityName.length > 0 && idString && idString.length > 0 && revisionString && !isNaN(+revisionString)) {
      this.entityKey.set(new EntityKey());
      this.entityKey().entityId = idString;
      this.entityKey().entityName = entityName;
      this.entityKey().entityRevision = +revisionString;
      this.modelService.getEngineKey(this.propertyName).subscribe(result => this.loadEngine(result), error => this.handleError(error));
    } else {
      this.goBack();
    }
  }

  private loadEngine(engineKey: string): void {
    this.resetStatus();
    this.engineKey.set(engineKey);
    this.specificationService.getSpecifications(this.entityKey()).subscribe(result => {
      if (result && result.length > 0) {
        let defaults = result.filter(specification => specification.specificationType == 'PRO_USC');
        if (defaults && defaults.length == 1) {
          this.modelService.loadConfiguration(this.engineKey(), defaults[0].specificationCharacteristics).subscribe(() => this.loadUserSelections());
        } else {
          this.loadUserSelections();
        }
      } else {
        this.loadUserSelections();
      }
    }, error => this.handleError(error));
  }

  private executeHierarchy(name: string): void {
    this.isBusy.set(true);
    this.modelService.executeHierarchy(this.engineKey(), name).subscribe(() => {
      this.loadSelectedValues();
    }, error => this.handleError(error));
  }

  private loadUserSelections(): void {
    this.isBusy.set(true);
    this.modelService.getUserSelections(this.engineKey()).subscribe(result => {
      this.userSelections.set(JSON.parse(JSON.stringify(result)));
      this.selectedValues.set(JSON.parse(JSON.stringify(result)));
      this.isBusy.set(false);
    }, error => this.handleError(error));
  }

  private loadSelectedValues(): void {
    this.isBusy.set(true);
    this.modelService.getUserSelections(this.engineKey()).subscribe(result => {
      this.selectedValues.set(result);
      this.modelService.getInvalidEntities(this.engineKey()).subscribe(result => {
        this.invalidEntities.set(result);
        this.modelService.isComplete(this.engineKey()).subscribe(result => {
          this.isComplete.set(result);
          this.modelService.isValid(this.engineKey()).subscribe(result => {
            this.isValid.set(result);
            this.isBusy.set(false);
          });
        });
      }, error => this.handleError(error));
    }, error => this.handleError(error));
  }

  public save(): void {
    this.resetStatus();
    this.modelService.getCharacteristics(this.engineKey()).subscribe(result => {
      if (result && result.length > 0) {
        this.specificationService.saveCharacteristics(this.entityKey(), result).subscribe(() => this.isBusy.set(false), error => this.handleError(error));
      }
    }, error => this.handleError(error));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.invalidEntities.set(undefined!);
    this.isComplete.set(false);
    this.isValid.set(false);
  }

  private backToEntity(): void {
    let pageName = this.entityKey().entityName === 'QUOTE' ? "quotes" : "orders";
    this.router.navigate([pageName, this.entityKey().entityId, this.entityKey().entityRevision]);
  }

  goBack(): void {
    if (this.entityKey() && this.entityKey().entityName) {
      this.backToEntity();
    } else {
      this.router.navigate(['main']);
    }
  }

  userHasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLoginService.getCachedLogin();
      this.initialize();
    }
  }

  getSelections(entityName: string): GuruAllowable[] {
    let filtered: GuruEntity[] = this.userSelections().filter(selection => selection.Name == entityName);
    if (filtered && filtered.length == 1) {
      let wrapper: GuruEntity = filtered[0];
      return wrapper.Allowables;
    }
    return new Array();
  }

  getCurrentValue(entityName: string): string {
    let filtered: GuruEntity[] = this.selectedValues().filter(selection => selection.Name == entityName);
    if (filtered && filtered.length == 1) {
      let wrapper: GuruEntity = filtered[0];
      return wrapper.Value;
    }
    return "";
  }

  validateSelections(entityName: string, changeEvent: any): void {
    this.resetStatus();
    this.modelService.setValue(entityName, this.engineKey(), changeEvent.target.value)
      .subscribe(() => {
        if (entityName == 'USRiserVendor') {
          this.executeHierarchy('hierRiserVendorDefault');
        } else {
          this.loadSelectedValues();
        }
      }, error => this.handleError(error));
  }

  isInvalid(entityName: string): boolean {
    return this.invalidEntities() && this.invalidEntities().length > 0 && this.invalidEntities().filter(entry => entry == entityName).length > 0;
  }

  isSelectionValid(entityName: string, value: string): boolean {
    let wrappers: GuruEntity[] = this.selectedValues().filter(selection => selection.Name == entityName);
    if (wrappers && wrappers.length == 1) {
      let selections = wrappers[0].Allowables;
      if (selections && selections.length > 0) {
        let filtered: GuruAllowable[] = selections.filter(selection => selection.Value == value);
        if (filtered && filtered.length == 1) {
          return filtered[0].IsValid;
        }
      }
    }
    return false;
  }

}
